import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from 'react-toastify';
import { api, useAuth } from '../services/AuthService';

const money = (cents) => (Number(cents || 0) / 100).toLocaleString('pt-BR', {
  style: 'currency', currency: 'BRL'
});

function StripeTestLab() {
  const { isAdminMaster } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [split, setSplit] = useState({
    lanchonete_id: '', motoboy_id: '', lanchonete_amount: '10.00', motoboy_amount: '3.00'
  });
  const [subscriptionStore, setSubscriptionStore] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get('/stripe-test');
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar laboratório Stripe');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('stripe_session_id');
    if (!sessionId) return;

    setBusy('finalize');
    api.post(`/stripe-test/split-checkout/${sessionId}/finalize`)
      .then(() => {
        toast.success('Pagamento e split de teste concluídos!');
        window.history.replaceState({}, '', window.location.pathname);
        load();
      })
      .catch((error) => toast.error(error.response?.data?.error || 'Erro ao concluir split de teste'))
      .finally(() => setBusy(''));
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subscriptionResult = params.get('stripe_subscription');
    if (!subscriptionResult) return;
    if (subscriptionResult === 'success') {
      toast.success('Mensalidade de teste criada com sucesso!');
      load();
    } else if (subscriptionResult === 'cancelled') {
      toast.info('Teste da mensalidade cancelado.');
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, [load]);

  const accountMap = useMemo(() => {
    const result = {};
    (data?.accounts || []).forEach((account) => {
      result[`${account.entity_type}-${account.entity_id}`] = account;
    });
    return result;
  }, [data]);

  const isStoreCourier = useCallback((motoboy) => (
    Boolean(motoboy?.lanchonete_id) || String(motoboy?.motoboy_scope || '').toUpperCase() === 'LOJA'
  ), []);

  const availableMotoboys = useMemo(() => (data?.motoboys || []).filter((motoboy) => (
    !isStoreCourier(motoboy) || Number(motoboy.lanchonete_id) === Number(split.lanchonete_id)
  )), [data, split.lanchonete_id, isStoreCourier]);

  const selectedMotoboy = useMemo(() => (
    (data?.motoboys || []).find((motoboy) => Number(motoboy.id) === Number(split.motoboy_id))
  ), [data, split.motoboy_id]);

  const startOnboarding = async (entityType, entityId) => {
    try {
      setBusy(`${entityType}-${entityId}`);
      const existing = accountMap[`${entityType}-${entityId}`];
      const account = existing || (await api.post('/stripe-test/accounts', {
        entity_type: entityType, entity_id: entityId
      })).data.account;
      const link = await api.post(`/stripe-test/accounts/${account.stripe_account_id}/onboarding-link`);
      window.location.href = link.data.url;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao abrir cadastro bancário de teste');
      setBusy('');
    }
  };

  const createSplitCheckout = async () => {
    try {
      setBusy('split');
      const response = await api.post('/stripe-test/split-checkout', split);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar pagamento de teste');
      setBusy('');
    }
  };

  const createSubscription = async () => {
    try {
      setBusy('subscription');
      const response = await api.post('/stripe-test/subscription-checkout', {
        lanchonete_id: subscriptionStore
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar assinatura de teste');
      setBusy('');
    }
  };

  if (!isAdminMaster()) return <Alert severity="error">Área exclusiva do Admin Master.</Alert>;
  if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Laboratório Stripe</Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Ambiente somente para testes. O Mercado Pago continua sendo o pagamento oficial do aplicativo e não foi alterado.
      </Alert>
      <Alert severity={data?.configured ? 'success' : 'error'} sx={{ mb: 3 }}>
        {data?.configured
          ? 'Stripe configurado com chave de teste. Nenhum dinheiro real será movimentado.'
          : 'Configure STRIPE_TEST_SECRET_KEY no servidor usando uma chave nova que comece com sk_test_.'}
      </Alert>

      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom><AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Contas para receber</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              O Stripe coleta os dados bancários com segurança. O aplicativo não armazena agência, conta ou documentos bancários.
            </Typography>

            <Typography fontWeight="bold" sx={{ mt: 2 }}>Lanchonetes</Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {(data?.lanchonetes || []).map((item) => {
                const connected = accountMap[`lanchonete-${item.id}`];
                return (
                  <Box key={item.id} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography>{item.name}</Typography>
                    <Button
                      size="small"
                      variant={connected ? 'outlined' : 'contained'}
                      endIcon={<OpenInNewIcon />}
                      disabled={Boolean(busy)}
                      onClick={() => startOnboarding('lanchonete', item.id)}
                    >
                      {connected ? 'Continuar cadastro bancário' : 'Conectar ao Stripe teste'}
                    </Button>
                  </Box>
                );
              })}
            </Stack>

            <Typography fontWeight="bold" sx={{ mt: 3 }}>Motoboys</Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {(data?.motoboys || []).map((item) => {
                const connected = accountMap[`motoboy-${item.id}`];
                const belongsToStore = isStoreCourier(item);
                return (
                  <Box key={item.id} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Box>
                      <Typography>{item.name}</Typography>
                      <Chip size="small" color={belongsToStore ? 'default' : 'primary'} label={belongsToStore ? 'Motoboy da lanchonete' : 'Motoboy do aplicativo'} />
                    </Box>
                    {belongsToStore ? (
                      <Typography variant="body2" color="text.secondary">Taxa vai para a lanchonete</Typography>
                    ) : (
                      <Button
                        size="small"
                        variant={connected ? 'outlined' : 'contained'}
                        endIcon={<OpenInNewIcon />}
                        disabled={Boolean(busy)}
                        onClick={() => startOnboarding('motoboy', item.id)}
                      >
                        {connected ? 'Continuar cadastro bancário' : 'Conectar ao Stripe teste'}
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom><CreditCardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Testar split de um pedido</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Motoboy do aplicativo recebe a taxa de entrega. Quando o motoboy pertence à lanchonete, o valor dos produtos e a taxa vão integralmente para a lanchonete.
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Lanchonete</InputLabel>
                <Select label="Lanchonete" value={split.lanchonete_id} onChange={(e) => setSplit({ ...split, lanchonete_id: e.target.value, motoboy_id: '' })}>
                  {(data?.lanchonetes || []).map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField fullWidth label="Valor da lanchonete" type="number" value={split.lanchonete_amount} onChange={(e) => setSplit({ ...split, lanchonete_amount: e.target.value })} />
              <FormControl fullWidth>
                <InputLabel>Motoboy</InputLabel>
                <Select label="Motoboy" value={split.motoboy_id} onChange={(e) => setSplit({ ...split, motoboy_id: e.target.value })}>
                  {availableMotoboys.map((item) => <MenuItem key={item.id} value={item.id}>{item.name} — {isStoreCourier(item) ? 'da lanchonete' : 'do aplicativo'}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField fullWidth label="Taxa de entrega" type="number" value={split.motoboy_amount} onChange={(e) => setSplit({ ...split, motoboy_amount: e.target.value })} />
            </Stack>
            {selectedMotoboy && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {isStoreCourier(selectedMotoboy)
                  ? 'Motoboy da lanchonete: o valor dos produtos e a taxa de entrega serão enviados para a lanchonete.'
                  : 'Motoboy do aplicativo: o valor dos produtos irá para a lanchonete e a taxa de entrega irá para o motoboy.'}
              </Alert>
            )}
            <Button sx={{ mt: 2 }} variant="contained" disabled={Boolean(busy) || !split.lanchonete_id || !split.motoboy_id} onClick={createSplitCheckout}>
              Abrir pagamento de teste — {(Number(split.lanchonete_amount || 0) + Number(split.motoboy_amount || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6">Testar mensalidade de R$ 59,90</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>Cria uma assinatura mensal apenas no ambiente de testes do Stripe.</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Lanchonete</InputLabel>
                <Select label="Lanchonete" value={subscriptionStore} onChange={(e) => setSubscriptionStore(e.target.value)}>
                  {(data?.lanchonetes || []).map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" disabled={!subscriptionStore || Boolean(busy)} onClick={createSubscription}>
                Testar assinatura
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Últimos testes de split</Typography>
            <Stack spacing={1}>
              {(data?.transactions || []).map((item) => (
                <Box key={item.id} display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip size="small" label={item.status} color={item.status === 'split_completed' ? 'success' : 'default'} />
                  <Typography variant="body2">{item.reference}</Typography>
                  <Typography variant="body2">
                    {item.delivery_recipient === 'lanchonete'
                      ? `Loja: ${money(item.total_amount)} | Motoboy da loja: não recebe transferência`
                      : `Loja: ${money(item.lanchonete_amount)} | Motoboy do aplicativo: ${money(item.motoboy_amount)}`}
                  </Typography>
                </Box>
              ))}
              {!data?.transactions?.length && <Typography color="text.secondary">Nenhum teste realizado.</Typography>}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Mensalidades testadas</Typography>
            <Stack spacing={1}>
              {(data?.test_subscriptions || []).map((subscription) => {
                const store = data?.lanchonetes?.find(
                  (item) => Number(item.id) === Number(subscription.lanchonete_id)
                );
                return (
                  <Box key={subscription.id} display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      size="small"
                      label={subscription.status}
                      color={subscription.status === 'active' ? 'success' : 'default'}
                    />
                    <Typography variant="body2">{store?.name || `Lanchonete ${subscription.lanchonete_id}`}</Typography>
                    <Typography variant="body2">{money(subscription.amount)} por mês</Typography>
                  </Box>
                );
              })}
              {!data?.test_subscriptions?.length && (
                <Typography color="text.secondary">Nenhuma mensalidade de teste encontrada.</Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default StripeTestLab;
